import React, { PropsWithChildren, ReactNode } from 'react';

import { Box, ButtonBase, Skeleton, Tooltip } from '@mui/material';
import { Shortcut } from '@apigrowfinance/pbolt-shortcut-provider';

// if shortcut, onFocus, onBlur ariaLabel are only allowed if onClick is present
type StatusBarItemProps = {
    isLoading?: boolean;
    tooltipText?: ReactNode;
    onClick?: () => void;
    shortcut?: string;
    onFocus?: () => void;
    onBlur?: () => void;
    ariaLabel?: string;
} & (
    | {
          onClick: () => void;
          shortcut?: string;
          onFocus?: () => void;
          onBlur?: () => void;
          ariaLabel?: string;
      }
    | {
          onClick?: never;
          shortcut?: never;
          onFocus?: () => never;
          onBlur?: () => never;
          ariaLabel?: never;
      }
);

const StatusBarItem = ({
    isLoading = false,
    tooltipText,
    children,
    shortcut,
    onClick,
    onFocus,
    onBlur,
    ariaLabel,
}: PropsWithChildren<StatusBarItemProps>) => {
    if (isLoading) {
        return (
            <Skeleton width={80} sx={{ mx: 2 }} data-testid="item-skeleton" />
        );
    }
    const positionContainer = (
        <Box display="flex" alignItems="center" mx={2}>
            {children}
        </Box>
    );
    const content = tooltipText ? (
        <Tooltip title={tooltipText} placement="top">
            {positionContainer}
        </Tooltip>
    ) : (
        positionContainer
    );
    if (onClick) {
        const button = (
            <ButtonBase
                data-testid="button-status-bar-item"
                onClick={onClick}
                onBlur={onBlur}
                onFocus={onFocus}
                onMouseEnter={onFocus}
                aria-label={ariaLabel}
                onMouseLeave={onBlur}>
                {content}
            </ButtonBase>
        );
        if (shortcut) {
            return (
                <Shortcut combination={shortcut} onPress={onClick}>
                    {button}
                </Shortcut>
            );
        }
        return button;
    }
    return content;
};

export default StatusBarItem;
